import { useState } from "react";
import {
  Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff,
  LayoutGrid, Zap, Star, Sparkles, Flame, Calculator, Users, Package,
  Headphones, Shirt, Home, Gamepad2, Smartphone, Pencil, Dumbbell,
  Car, PawPrint, MoreHorizontal, Plane, Ship, GripVertical,
} from "lucide-react";
import ImageUploadInput from "../../components/ImageUploadInput";

// ── Shared helpers ────────────────────────────────────────────────────────────
function uid() { return `id_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`; }

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-600">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white text-slate-800"
    />
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
        value ? "bg-orange-50 border-orange-300 text-orange-700" : "bg-slate-100 border-slate-200 text-slate-500"
      }`}>
      {value ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      {label || (value ? "Enabled" : "Disabled")}
    </button>
  );
}

// All available icon names for nav items
const NAV_ICON_OPTIONS = [
  "LayoutGrid", "Zap", "Star", "Sparkles", "Flame", "Calculator", "Users",
  "Package", "Headphones", "Shirt", "Home", "Gamepad2", "Smartphone", "Pencil",
  "Dumbbell", "Car", "PawPrint", "MoreHorizontal", "Plane", "Ship", "Heart", "Search",
];

const ICON_MAP = {
  LayoutGrid, Zap, Star, Sparkles, Flame, Calculator, Users, Package, Headphones,
  Shirt, Home, Gamepad2, Smartphone, Pencil, Dumbbell, Car, PawPrint, MoreHorizontal,
  Plane, Ship,
};

function IconPicker({ value, onChange }) {
  return (
    <select
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white text-slate-800">
      <option value="">— None —</option>
      {NAV_ICON_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

// ── Header Tab ────────────────────────────────────────────────────────────────
export default function HeaderAdmin({ data = {}, onChange }) {
  const [tab, setTab] = useState("header");

  const header = data.header || {};
  const megaMenu = data.mega_menu || {};
  const setHeader = (patch) => onChange({ ...data, header: { ...header, ...patch } });
  const setMega = (patch) => onChange({ ...data, mega_menu: { ...megaMenu, ...patch } });

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-5 w-fit">
        {[
          { id: "header", label: "Header Setup" },
          { id: "navbar", label: "Nav Bar Items" },
          { id: "megamenu", label: "Mega Menu" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Header Setup ── */}
      {tab === "header" && (
        <div className="space-y-6">
          {/* CTA Button */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-700">CTA Button (e.g. "Import from China")</p>
              <Toggle
                value={header.cta_button?.enabled !== false}
                onChange={v => setHeader({ cta_button: { ...header.cta_button, enabled: v } })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Button Text">
                <Input
                  value={header.cta_button?.text}
                  onChange={v => setHeader({ cta_button: { ...header.cta_button, text: v } })}
                  placeholder="Import from China"
                />
              </Field>
              <Field label="Button URL">
                <Input
                  value={header.cta_button?.url}
                  onChange={v => setHeader({ cta_button: { ...header.cta_button, url: v } })}
                  placeholder="/pages/import"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Left Icon (custom upload — overrides plane icon)">
                <ImageUploadInput
                  value={header.cta_button?.icon1_url || ""}
                  onChange={v => setHeader({ cta_button: { ...header.cta_button, icon1_url: v } })}
                  placeholder="Upload or paste icon URL"
                />
              </Field>
              <Field label="Right Icon (custom upload — overrides ship icon)">
                <ImageUploadInput
                  value={header.cta_button?.icon2_url || ""}
                  onChange={v => setHeader({ cta_button: { ...header.cta_button, icon2_url: v } })}
                  placeholder="Upload or paste icon URL"
                />
              </Field>
            </div>
          </div>

          {/* Account / Wishlist / Cart toggles */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-bold text-slate-700">Header Icon Sections</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col gap-2 items-start">
                <p className="text-xs font-semibold text-slate-600">Account</p>
                <Toggle
                  value={header.account?.enabled !== false}
                  onChange={v => setHeader({ account: { ...header.account, enabled: v } })}
                />
              </div>
              <div className="flex flex-col gap-2 items-start">
                <p className="text-xs font-semibold text-slate-600">Wishlist</p>
                <Toggle
                  value={header.wishlist?.enabled !== false}
                  onChange={v => setHeader({ wishlist: { ...header.wishlist, enabled: v } })}
                />
              </div>
              <div className="flex flex-col gap-2 items-start">
                <p className="text-xs font-semibold text-slate-600">Cart</p>
                <Toggle
                  value={header.cart?.enabled !== false}
                  onChange={v => setHeader({ cart: { ...header.cart, enabled: v } })}
                />
              </div>
            </div>
          </div>

          {/* Search bar */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-700">Search Bar</p>
              <Toggle
                value={header.search?.enabled !== false}
                onChange={v => setHeader({ search: { ...header.search, enabled: v } })}
              />
            </div>
            <Field label="Placeholder Text">
              <Input
                value={header.search?.placeholder}
                onChange={v => setHeader({ search: { ...header.search, placeholder: v } })}
                placeholder="Search for Products, Brands and more..."
              />
            </Field>
          </div>
        </div>
      )}

      {/* ── Nav Bar Items ── */}
      {tab === "navbar" && (
        <NavItemsEditor
          items={header.nav_items || []}
          onChange={items => setHeader({ nav_items: items })}
        />
      )}

      {/* ── Mega Menu ── */}
      {tab === "megamenu" && (
        <MegaMenuEditor data={megaMenu} onChange={setMega} />
      )}
    </div>
  );
}

// ── Nav Items Editor ──────────────────────────────────────────────────────────
function NavItemsEditor({ items, onChange }) {
  const [expanded, setExpanded] = useState(null);

  const move = (idx, dir) => {
    const arr = [...items];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    onChange(arr);
  };

  const update = (idx, patch) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], ...patch };
    onChange(arr);
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const add = () => {
    onChange([...items, {
      id: uid(), label: "New Link", url: "/", enabled: true, icon_name: "Package", icon_url: "", badge: "",
    }]);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Drag to reorder · Toggle to show/hide · Edit label, URL and icon</p>

      {items.map((item, idx) => {
        const isOpen = expanded === idx;
        const IconComp = ICON_MAP[item.icon_name];
        return (
          <div key={item.id || idx} className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-white">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>

              {IconComp && <IconComp className="w-4 h-4 text-orange-500 flex-shrink-0" />}
              {item.icon_url && !IconComp && <img src={item.icon_url} alt="" className="w-4 h-4 object-contain flex-shrink-0" />}

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{item.label}</p>
                {item.type === "mega-menu" && <span className="text-[9px] text-orange-600 font-bold">MEGA MENU</span>}
              </div>

              {item.badge && (
                <span className="text-[9px] font-bold px-2 py-0.5 rounded text-white flex-shrink-0"
                  style={{ backgroundColor: item.badge_color || "#2563eb" }}>
                  {item.badge}
                </span>
              )}

              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Toggle value={item.enabled !== false} onChange={v => update(idx, { enabled: v })} label={item.enabled !== false ? "On" : "Off"} />
                <button onClick={() => setExpanded(isOpen ? null : idx)}
                  className="text-xs text-orange-600 font-semibold px-2 py-1 rounded hover:bg-orange-50 transition-colors">
                  {isOpen ? "Done" : "Edit"}
                </button>
                {item.type !== "mega-menu" && (
                  <button onClick={() => remove(idx)} className="text-red-400 hover:text-red-600 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {isOpen && (
              <div className="px-4 pb-4 pt-2 bg-slate-50 border-t border-slate-100 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Label">
                    <Input value={item.label} onChange={v => update(idx, { label: v })} />
                  </Field>
                  {item.type !== "mega-menu" && (
                    <Field label="URL / Link">
                      <Input value={item.url} onChange={v => update(idx, { url: v })} placeholder="/page-or-url" />
                    </Field>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Icon Name" hint="Pick from list or leave blank">
                    <IconPicker value={item.icon_name} onChange={v => update(idx, { icon_name: v })} />
                  </Field>
                  <Field label="Custom Icon URL (overrides above)">
                    <ImageUploadInput
                      value={item.icon_url || ""}
                      onChange={v => update(idx, { icon_url: v })}
                      placeholder="Upload or paste icon URL"
                    />
                  </Field>
                </div>
                {item.type !== "mega-menu" && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Badge Text (e.g. NEW, HOT)" hint="Leave blank for no badge">
                      <Input value={item.badge} onChange={v => update(idx, { badge: v })} placeholder="NEW" />
                    </Field>
                    <Field label="Badge Colour">
                      <div className="flex items-center gap-2">
                        <input type="color" value={item.badge_color || "#2563eb"}
                          onChange={e => update(idx, { badge_color: e.target.value })}
                          className="h-9 w-16 rounded border border-slate-200 cursor-pointer" />
                        <Input value={item.badge_color} onChange={v => update(idx, { badge_color: v })} placeholder="#2563eb" />
                      </div>
                    </Field>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={!!item.separator_before}
                      onChange={e => update(idx, { separator_before: e.target.checked })}
                      className="w-3.5 h-3.5" />
                    Add divider line before this item
                  </label>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={add}
        className="w-full py-2 border-2 border-dashed border-orange-300 text-orange-600 text-sm font-semibold rounded-xl hover:bg-orange-50 transition-colors flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Add Nav Item
      </button>
    </div>
  );
}

// ── Mega Menu Editor ──────────────────────────────────────────────────────────
function MegaMenuEditor({ data, onChange }) {
  const [tab, setTab] = useState("categories");
  const [expandedCat, setExpandedCat] = useState(null);

  const cats = data.categories || [];
  const creative = data.creative || {};
  const shopBy = data.shop_by_category || {};

  const updateCats = (c) => onChange({ ...data, categories: c });
  const updateCreative = (patch) => onChange({ ...data, creative: { ...creative, ...patch } });
  const updateShopBy = (patch) => onChange({ ...data, shop_by_category: { ...shopBy, ...patch } });

  const moveCat = (idx, dir) => {
    const arr = [...cats];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    updateCats(arr);
  };

  const updateCat = (idx, patch) => {
    const arr = [...cats];
    arr[idx] = { ...arr[idx], ...patch };
    updateCats(arr);
  };

  const addCat = () => {
    updateCats([...cats, { id: uid(), label: "New Category", icon_name: "Package", icon_url: "", url: "/", enabled: true, sub_groups: [] }]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-700">Mega Menu (All Categories dropdown)</p>
        <Toggle value={data.enabled !== false} onChange={v => onChange({ ...data, enabled: v })} />
      </div>

      {/* Inner sub-tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit text-sm">
        {[
          { id: "categories", label: "Categories" },
          { id: "creative", label: "Creative Banner" },
          { id: "shopby", label: "Shop by Category" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Categories */}
      {tab === "categories" && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">Manage the categories shown in the left column of the mega menu. Click "Edit" to add/edit sub-category groups and items.</p>
          {cats.map((cat, idx) => {
            const isOpen = expandedCat === idx;
            const IconComp = ICON_MAP[cat.icon_name];
            return (
              <div key={cat.id || idx} className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-white">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveCat(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => moveCat(idx, 1)} disabled={idx === cats.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                  <span className="text-orange-500">
                    {cat.icon_url
                      ? <img src={cat.icon_url} alt="" className="w-4 h-4 object-contain" />
                      : IconComp ? <IconComp className="w-4 h-4" /> : null
                    }
                  </span>
                  <span className="flex-1 text-sm font-semibold text-slate-800">{cat.label}</span>
                  <span className="text-xs text-slate-400">{(cat.sub_groups || []).length} groups</span>
                  <Toggle value={cat.enabled !== false} onChange={v => updateCat(idx, { enabled: v })} label={cat.enabled !== false ? "On" : "Off"} />
                  <button onClick={() => setExpandedCat(isOpen ? null : idx)}
                    className="text-xs text-orange-600 font-semibold px-2 py-1 rounded hover:bg-orange-50 transition-colors">
                    {isOpen ? "Done" : "Edit"}
                  </button>
                  <button onClick={() => updateCats(cats.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {isOpen && (
                  <div className="p-4 bg-slate-50 border-t border-slate-100 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Category Label">
                        <Input value={cat.label} onChange={v => updateCat(idx, { label: v })} />
                      </Field>
                      <Field label="Category URL">
                        <Input value={cat.url} onChange={v => updateCat(idx, { url: v })} placeholder="/c/category-slug" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Icon Name">
                        <IconPicker value={cat.icon_name} onChange={v => updateCat(idx, { icon_name: v })} />
                      </Field>
                      <Field label="Custom Icon URL (overrides icon)">
                        <ImageUploadInput value={cat.icon_url || ""} onChange={v => updateCat(idx, { icon_url: v })} placeholder="Upload or paste" />
                      </Field>
                    </div>

                    {/* Sub-groups */}
                    <SubGroupsEditor
                      groups={cat.sub_groups || []}
                      onChange={g => updateCat(idx, { sub_groups: g })}
                    />
                  </div>
                )}
              </div>
            );
          })}

          <button onClick={addCat}
            className="w-full py-2 border-2 border-dashed border-orange-300 text-orange-600 text-sm font-semibold rounded-xl hover:bg-orange-50 transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Add Category
          </button>
        </div>
      )}

      {/* Creative Banner */}
      {tab === "creative" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Banner visible in mega menu right panel</p>
            <Toggle value={creative.enabled !== false} onChange={v => updateCreative({ enabled: v })} />
          </div>
          <Field label="Media Type">
            <select value={creative.type || "image"} onChange={e => updateCreative({ type: e.target.value })}
              className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white">
              <option value="image">Image</option>
              <option value="video">Video / GIF</option>
            </select>
          </Field>
          <Field label={creative.type === "video" ? "Video / GIF URL" : "Image URL"}>
            <ImageUploadInput value={creative.url || ""} onChange={v => updateCreative({ url: v })} placeholder="Upload or paste URL" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title">
              <Input value={creative.title} onChange={v => updateCreative({ title: v })} placeholder="Fashion Collection" />
            </Field>
            <Field label="Subtitle">
              <Input value={creative.subtitle} onChange={v => updateCreative({ subtitle: v })} placeholder="Trendy Styles for Everyone" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Button Text">
              <Input value={creative.cta_text} onChange={v => updateCreative({ cta_text: v })} placeholder="Shop Now →" />
            </Field>
            <Field label="Button URL">
              <Input value={creative.cta_url} onChange={v => updateCreative({ cta_url: v })} placeholder="/c/fashion-apparel" />
            </Field>
          </div>
        </div>
      )}

      {/* Shop by Category tiles */}
      {tab === "shopby" && (
        <ShopByCategoryEditor data={shopBy} onChange={updateShopBy} />
      )}
    </div>
  );
}

// ── Sub Groups Editor ─────────────────────────────────────────────────────────
function SubGroupsEditor({ groups, onChange }) {
  const [expanded, setExpanded] = useState(null);

  const addGroup = () => onChange([...groups, { title: "New Group", items: [] }]);
  const removeGroup = (i) => onChange(groups.filter((_, idx) => idx !== i));
  const updateGroup = (i, patch) => { const g = [...groups]; g[i] = { ...g[i], ...patch }; onChange(g); };

  const addItem = (gi) => {
    const g = [...groups];
    g[gi] = { ...g[gi], items: [...(g[gi].items || []), { label: "New Item", url: "" }] };
    onChange(g);
  };
  const removeItem = (gi, ii) => {
    const g = [...groups];
    g[gi] = { ...g[gi], items: g[gi].items.filter((_, idx) => idx !== ii) };
    onChange(g);
  };
  const updateItem = (gi, ii, patch) => {
    const g = [...groups];
    g[gi] = { ...g[gi], items: g[gi].items.map((it, idx) => idx === ii ? { ...it, ...patch } : it) };
    onChange(g);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Sub-Category Groups</p>
      {groups.map((group, gi) => (
        <div key={gi} className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-white">
            <span className="flex-1 text-xs font-semibold text-slate-700">{group.title}</span>
            <span className="text-xs text-slate-400">{(group.items || []).length} items</span>
            <button onClick={() => setExpanded(expanded === gi ? null : gi)}
              className="text-xs text-orange-600 font-semibold px-2 py-1 rounded hover:bg-orange-50">
              {expanded === gi ? "Done" : "Edit"}
            </button>
            <button onClick={() => removeGroup(gi)} className="text-red-400 hover:text-red-600 p-1">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          {expanded === gi && (
            <div className="p-3 bg-slate-50 border-t border-slate-100 space-y-2">
              <Field label="Group Title">
                <Input value={group.title} onChange={v => updateGroup(gi, { title: v })} />
              </Field>
              <p className="text-[10px] font-bold text-slate-500 uppercase">Items</p>
              {(group.items || []).map((item, ii) => (
                <div key={ii} className="flex items-center gap-2">
                  <input
                    value={item.label}
                    onChange={e => updateItem(gi, ii, { label: e.target.value })}
                    placeholder="Item label"
                    className="flex-1 h-8 px-2 border border-slate-200 rounded-lg text-xs outline-none focus:border-orange-400 bg-white"
                  />
                  <input
                    value={item.url}
                    onChange={e => updateItem(gi, ii, { url: e.target.value })}
                    placeholder="URL (optional)"
                    className="flex-1 h-8 px-2 border border-slate-200 rounded-lg text-xs outline-none focus:border-orange-400 bg-white"
                  />
                  <button onClick={() => removeItem(gi, ii)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => addItem(gi)}
                className="text-xs text-orange-600 font-semibold flex items-center gap-1 hover:underline">
                <Plus className="w-3 h-3" /> Add Item
              </button>
            </div>
          )}
        </div>
      ))}
      <button onClick={addGroup}
        className="w-full py-1.5 border border-dashed border-orange-300 text-orange-600 text-xs font-semibold rounded-lg hover:bg-orange-50 flex items-center justify-center gap-1.5">
        <Plus className="w-3 h-3" /> Add Sub-group
      </button>
    </div>
  );
}

// ── Shop by Category Editor ───────────────────────────────────────────────────
function ShopByCategoryEditor({ data, onChange }) {
  const items = data.items || [];
  const addItem = () => onChange({ ...data, items: [...items, { label: "New", image_url: "", url: "" }] });
  const removeItem = (i) => onChange({ ...data, items: items.filter((_, idx) => idx !== i) });
  const updateItem = (i, patch) => {
    const arr = [...items];
    arr[i] = { ...arr[i], ...patch };
    onChange({ ...data, items: arr });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">Product image tiles shown below subcategories</p>
        <Toggle value={data.enabled !== false} onChange={v => onChange({ ...data, enabled: v })} />
      </div>
      <Field label="Section Title">
        <input value={data.title || ""} onChange={e => onChange({ ...data, title: e.target.value })}
          placeholder="Shop by Category"
          className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white" />
      </Field>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-2 border border-slate-200 rounded-lg bg-white">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
              {item.image_url
                ? <img src={item.image_url} alt={item.label} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">{item.label?.[0]}</div>
              }
            </div>
            <div className="flex-1 space-y-1">
              <input value={item.label} onChange={e => updateItem(i, { label: e.target.value })}
                placeholder="Label (e.g. Hoodies)"
                className="w-full h-7 px-2 border border-slate-200 rounded text-xs outline-none focus:border-orange-400 bg-white" />
              <input value={item.url} onChange={e => updateItem(i, { url: e.target.value })}
                placeholder="URL (e.g. /search?q=hoodies)"
                className="w-full h-7 px-2 border border-slate-200 rounded text-xs outline-none focus:border-orange-400 bg-white" />
            </div>
            <div className="flex-shrink-0">
              <ImageUploadInput value={item.image_url || ""} onChange={v => updateItem(i, { image_url: v })} placeholder="Upload" />
            </div>
            <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 flex-shrink-0">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button onClick={addItem}
          className="w-full py-2 border-2 border-dashed border-orange-300 text-orange-600 text-sm font-semibold rounded-xl hover:bg-orange-50 flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" /> Add Tile
        </button>
      </div>
    </div>
  );
}
